/**
 * 量測輸入。依量測點的 `granularity` 自動切換逐片／整批。
 *
 * §9 規則 1  `WeighEvent.panelId` 批次量測為 null、逐片量測填值。
 *            **這個欄位是整套系統的存在理由**
 * §9 規則 2  粒度決定畫面是逐片還是整批。**切換只改設定，不改程式碼**
 * §9 規則 3  **逐片量測 ≠ 每片一個料件**。整車搬 + 逐片秤是必須支援的組合
 * §9 規則 8  數字一律以 kg 儲存，小數位由量測點設定控制顯示
 *
 * ── 與 mockup 的兩個差異 ────────────────────────────────────────────────
 * 1. 不再從 `world.panels` 讀片的中介資料，改由呼叫端把 `panels` 傳進來
 *    （後端讀的是投影表，沒有 World）。
 * 2. **移除「示範捷徑：自動填滿」**。mockup 用它快速填 200 筆做 demo；
 *    正式站台上那等於偽造量測值，是這套系統最不能有的東西。
 *
 * §10.4：逐片輸入 200 筆是這套系統最怕重來的操作，所以已輸入的值存在
 * `sessionStorage`，重整後還在，過帳成功才清除。
 */

import { useEffect, useMemo } from 'react'
import type { Config, MeasurementPoint } from '../../../shared/types/config.js'
import type { PanelId } from '../../../shared/types/domain.js'
import { materialRatio, specOf } from '../../../shared/derive/world.js'
import { count, kg, kgUnit, round } from '../../../shared/lib/format.js'
import { tareForContainer, weighNetKg, type WeighValue } from './weighValue.js'
import { GranularityBadge } from './badges.js'
import { NumericKeypadInput } from './NumericKeypadInput.js'
import { Alert, Field, Mono, Pill, Select, Table, Td } from './ui.js'

/** 呼叫端要提供的片中介資料。judgePick 與離群判定都靠它。 */
export interface PanelMeta {
  panel_id: string
  model_code: string | null
  regulatory_id: string | null
  condition_grade: string
}

// 純邏輯放在 weighValue.ts——它是前後端之間的契約，要能用 Node 直接測
export {
  emptyWeighValue, tareForContainer, toWeighPayload, weighComplete, weighNetKg,
  type WeighValue,
} from './weighValue.js'

/**
 * §10.4 逐片輸入的本地暫存。
 * 「已輸入的值存在瀏覽器 sessionStorage，重整後還在。過帳成功才清除。」
 * 這不是加分項，是必要項——200 筆重打一次沒有人受得了。
 */
export function useWeighDraft(key: string, value: WeighValue, onChange: (v: WeighValue) => void) {
  const storageKey = `pv.weigh.${key}`

  useEffect(() => {
    if (!key) return
    try {
      const raw = sessionStorage.getItem(storageKey)
      if (raw) onChange({ ...value, ...JSON.parse(raw) })
    } catch { /* 無痕視窗、或被清掉了 —— 沒有暫存也要能用 */ }
    // 只在掛載時還原一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  useEffect(() => {
    if (!key) return
    try { sessionStorage.setItem(storageKey, JSON.stringify(value)) } catch { /* 存不進去就算了 */ }
  }, [storageKey, key, value])

  return {
    clear: () => { try { sessionStorage.removeItem(storageKey) } catch { /* noop */ } },
  }
}

export function WeighEntry({
  mp, panels, config, value, onChange, materialCode,
}: {
  mp: MeasurementPoint
  panels: PanelMeta[]
  config: Config
  value: WeighValue
  onChange: (v: WeighValue) => void
  materialCode: string
}) {
  const panelIds = panels.map((p) => p.panel_id)

  const stats = useMemo(() => {
    const vals = panelIds.map((p) => Number(value.perPanel[p])).filter((n) => !Number.isNaN(n) && n !== 0)
    if (vals.length === 0) return null
    const sum = vals.reduce((a, b) => a + b, 0)
    return {
      n: vals.length,
      sum: round(sum, 3),
      avg: round(sum / vals.length, 3),
      max: Math.max(...vals),
      min: Math.min(...vals),
    }
  }, [panelIds, value.perPanel])

  /** 該片該物料的理論重。離群標示的基準（§7.5：數值上下限的顏色回饋留在前端） */
  const theoreticalOf = (meta: PanelMeta) => {
    const spec = specOf(config, meta.model_code)
    if (!spec) return null
    return round(spec.totalKg * materialRatio(spec, materialCode as never), 3)
  }

  const scales = config.scales.filter((s) => s.enabled)

  // weightSource = 'booked'：不量測，重量沿用來源料件（唯讀）
  if (mp.weightSource === 'booked') {
    return (
      <Alert tone="info" title="本量測點為帳面繼承">
        量測點 <Mono>{mp.measurementPointId}</Mono> 設定為帳面繼承，不需輸入重量
        ——重量沿用來源料件的實測值，由系統按基準比例切分。
      </Alert>
    )
  }

  if (!mp.enabled) {
    return (
      <Alert tone="warn" title="這個量測點目前停用">
        量測點 <Mono>{mp.measurementPointId}</Mono> 是停用狀態，所以這一項不會有重量。
        站台結算會顯示「無法結算」並說明原因——系統不會替您把數字湊出來。
        若現場已經有對應的秤，請主管到作業設定啟用它。
      </Alert>
    )
  }

  // ── §9 規則 2：逐片模式 ─────────────────────────────────────────────────
  if (mp.granularity === 'panel') {
    const entered = panelIds.filter((p) => value.perPanel[p]?.trim()).length
    const tol = config.tolerances.outlierPct

    return (
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <GranularityBadge g="panel" mpId={mp.measurementPointId} />
          <span className="text-xs text-ink-500">
            每筆各記一個量測事件（panelId 有值）
          </span>
          <span className="ml-auto text-sm font-medium tabular-nums">
            已輸入 {count(entered)} / {count(panelIds.length)}
          </span>
        </div>

        <div className="grid gap-3 lg:grid-cols-[1fr,220px]">
          <div className="max-h-[26rem] overflow-y-auto rounded-md border border-ink-200">
            <Table head={['#', 'PV 號', '重量', '狀態']} dense>
              {panels.map((meta, i) => {
                const p = meta.panel_id
                const raw = value.perPanel[p] ?? ''
                const th = theoreticalOf(meta)
                const n = raw.trim() === '' ? null : Number(raw)
                const outlier = th !== null && th > 0 && n !== null && Math.abs(n - th) / th > tol
                return (
                  <tr key={p} className={outlier ? 'bg-amber-50' : ''}>
                    <Td className="w-10 pl-2 tabular-nums text-ink-400">{i + 1}</Td>
                    <Td>
                      <Mono>{p}</Mono>
                      {meta.condition_grade === 'damaged' && (
                        <span title="破損" className="ml-1 text-red-600">破</span>
                      )}
                    </Td>
                    <Td className="w-[150px]">
                      <NumericKeypadInput
                        value={raw}
                        decimals={mp.decimals}
                        soft={{ min: mp.softMinKg, max: mp.softMaxKg }}
                        dataIdx={i}
                        onChange={(v) => onChange({ ...value, perPanel: { ...value.perPanel, [p]: v } })}
                        onEnter={() => {
                          const next = document.querySelector<HTMLInputElement>(`[data-weigh-idx="${i + 1}"]`)
                          next?.focus()
                          next?.select()
                        }}
                      />
                    </Td>
                    <Td className="w-20">
                      {raw.trim() === ''
                        ? <span className="text-xs text-ink-400">待輸入</span>
                        : outlier
                          ? <Pill tone="amber" title={`理論重 ${kg(th, 2)} kg，偏離超過 ±${tol * 100}%`}>離群</Pill>
                          : <span className="text-emerald-600">✓</span>}
                    </Td>
                  </tr>
                )
              })}
            </Table>
          </div>

          <aside className="sticky top-2 h-fit rounded-md border border-ink-200 bg-ink-50 p-3">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">即時統計</h4>
            {stats ? (
              <dl className="space-y-1 text-sm tabular-nums">
                <Row k="筆數" v={count(stats.n)} />
                <Row k="合計" v={kgUnit(stats.sum, mp.decimals)} strong />
                <Row k="平均" v={kgUnit(stats.avg, mp.decimals)} />
                <Row k="最大" v={kgUnit(stats.max, mp.decimals)} />
                <Row k="最小" v={kgUnit(stats.min, mp.decimals)} />
              </dl>
            ) : <p className="text-sm text-ink-400">尚無資料</p>}
            <p className="mt-3 border-t border-ink-200 pt-2 text-[11px] text-ink-500">
              已輸入的值會暫存在這台裝置上，重新整理不會消失。過帳成功後才清除。
            </p>
          </aside>
        </div>

        <div className="mt-3">
          <Field label="秤具">
            <Select value={value.scaleId} onChange={(e) => onChange({ ...value, scaleId: e.target.value })}>
              <option value="">請選擇</option>
              {scales.map((s) => <option key={s.scaleId} value={s.scaleId}>{s.label}</option>)}
            </Select>
          </Field>
        </div>

        {mp.requiresDualCheck && (
          <div className="mt-2">
            <Field label="覆核人" hint="本量測點要求雙人覆核，請由第二位人員確認後填入代號" required>
              <input
                value={value.verifiedBy}
                onChange={(e) => onChange({ ...value, verifiedBy: e.target.value })}
                className="w-full max-w-[200px] rounded border border-ink-300 px-2 py-1.5 text-sm"
              />
            </Field>
          </div>
        )}

        {entered > 0 && entered < panelIds.length && (
          <div className="mt-2">
            <Alert tone="warn" title="完整性檢核未通過">
              逐片模式下事件數必須等於片數（{count(entered)} / {count(panelIds.length)}），
              未輸入完不得過帳。
            </Alert>
          </div>
        )}
      </div>
    )
  }

  // ── 整批模式 ────────────────────────────────────────────────────────────
  const container = config.containers.find((c) => c.containerId === value.containerId)
  const net = weighNetKg(mp, value, panelIds)

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <GranularityBadge g="item" mpId={mp.measurementPointId} />
        <span className="text-xs text-ink-500">整批一個量測事件（panelId = null）</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="容器">
          <Select
            value={value.containerId}
            onChange={(e) => onChange({
              ...value,
              containerId: e.target.value,
              tareKg: tareForContainer(config, e.target.value),
            })}
          >
            <option value="">請選擇</option>
            {config.containers
              .filter((c) => !c.forMaterials || c.forMaterials.includes(materialCode as never))
              .map((c) => <option key={c.containerId} value={c.containerId}>{c.label}</option>)}
          </Select>
        </Field>
        <Field
          label="皮重"
          hint={container?.tareVaries
            ? '此容器皮重不一致，需逐次輸入'
            : container ? '由容器主檔帶入' : undefined}
        >
          <NumericKeypadInput
            value={value.tareKg}
            decimals={mp.decimals}
            disabled={Boolean(container) && !container!.tareVaries && container!.tareKg !== null}
            onChange={(v) => onChange({ ...value, tareKg: v })}
          />
        </Field>
        <Field label="毛重" required>
          <NumericKeypadInput
            value={value.grossKg}
            decimals={mp.decimals}
            keypad
            soft={{ min: mp.softMinKg, max: mp.softMaxKg }}
            onChange={(v) => onChange({ ...value, grossKg: v })}
          />
        </Field>
        <Field label="秤具" required>
          <Select value={value.scaleId} onChange={(e) => onChange({ ...value, scaleId: e.target.value })}>
            <option value="">請選擇</option>
            {scales.map((s) => <option key={s.scaleId} value={s.scaleId}>{s.label}</option>)}
          </Select>
        </Field>
      </div>

      {mp.requiresDualCheck && (
        <div className="mt-3">
          <Field label="覆核人" hint="本量測點要求雙人覆核" required>
            <input
              value={value.verifiedBy}
              onChange={(e) => onChange({ ...value, verifiedBy: e.target.value })}
              className="w-full max-w-[200px] rounded border border-ink-300 px-2 py-1.5 text-sm"
            />
          </Field>
        </div>
      )}

      <p className="mt-3 text-sm">
        淨重 <strong className="text-base tabular-nums">{kgUnit(net, mp.decimals)}</strong>
        {net !== null && net < 0 && <span className="ml-2 text-red-700">淨重為負，請確認皮重</span>}
      </p>
    </div>
  )
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-ink-500">{k}</dt>
      <dd className={strong ? 'font-semibold' : ''}>{v}</dd>
    </div>
  )
}
